import { useEffect, useState } from 'react';
import { Target } from 'lucide-react';

import SectionKicker from '@/components/landing/v3/SectionKicker';
import AuditorSeo from '@/components/landing/auditor/AuditorSeo';
import { getAuditStatus } from '@/api/publicAuditService';

/**
 * PuntuacionSeoV3 — la sección que explica la puntuación de 0 a 100 y, al
 * final, deja probarla con la ficha real del visitante.
 *
 * La caja ámbar del diseño trae un `<form>` con un campo y un botón «Ver mi
 * puntuación» cuyo `onSubmit` es un `preventDefault()` vacío. Eso ya existe de
 * verdad: es el **auditor SEO público** (`AuditorSeo`), que hoy vive en
 * `/auditor` sin estar enlazado desde ningún sitio. Aquí se monta por fin donde
 * el diseño lo pedía, en vez de reimplementar una caja de búsqueda que no
 * buscaría nada.
 *
 * `AuditorSeo` ya se esconde solo con la función apagada, pero la caja ámbar que
 * lo envuelve —con su titular y su «Gratis · sin registrarte»— no sabe nada de
 * eso y se quedaría pintada y vacía. Por eso la sección pregunta también por el
 * interruptor y monta la caja entera o ninguna.
 *
 * La respuesta de `/public/audit/status` lleva `Cache-Control: public,
 * max-age=300`, así que preguntar dos veces no es dos peticiones.
 */
export default function PuntuacionSeoV3({ c }) {
  const auditorActivo = useAuditorActivo();

  return (
    <section className="max-w-[1180px] mx-auto px-6 pt-16 md:pt-[76px]">
      <SectionKicker>{c.kicker}</SectionKicker>

      <div className="grid gap-6 mt-7 md:grid-cols-2">
        <div className="bg-v3-surface rounded-[20px] p-6 md:p-8">
          <h3 className="text-[22px] md:text-[24px] leading-[1.2] tracking-[-.02em] font-bold text-v3-ink">
            {c.hoy.titulo}
          </h3>
          <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3 max-w-[44ch]">
            {c.hoy.cuerpo}
          </p>

          <div className="flex items-center gap-5 bg-white rounded-2xl p-5 md:p-[22px] mt-6 v3-shadow flex-wrap">
            <div
              aria-hidden="true"
              className="w-28 h-28 rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'conic-gradient(#feae2c 0 62%, #ececea 62% 100%)' }}
            >
              <div className="w-[86px] h-[86px] rounded-full bg-white flex flex-col items-center justify-center">
                <span className="text-[32px] font-bold tracking-[-.03em] leading-none text-v3-ink">
                  62
                </span>
                <span className="text-[10.5px] text-v3-faint mt-0.5">/ 100</span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11.5px] font-semibold text-v3-faint">{c.hoy.ejemplo}</div>
              <div className="text-[22px] font-bold tracking-[-.02em] mt-1 text-v3-ink">
                {c.hoy.nivel}
              </div>
              <div className="text-[13.5px] text-v3-body mt-1.5">{c.hoy.pie}</div>
            </div>
          </div>
        </div>

        <div className="bg-v3-surface rounded-[20px] p-6 md:p-8">
          <h3 className="text-[22px] md:text-[24px] leading-[1.2] tracking-[-.02em] font-bold text-v3-ink">
            {c.falta.titulo}
          </h3>
          <p className="text-[15.5px] leading-[1.65] text-v3-body mt-3 max-w-[46ch]">
            {c.falta.cuerpo}
          </p>

          <div className="mt-6 flex flex-col gap-2.5">
            <div className="bg-white border-2 border-v3-gold rounded-2xl p-5 flex items-center gap-4 v3-shadow">
              <div className="w-[42px] h-[42px] rounded-xl bg-v3-goldsoft flex items-center justify-center shrink-0">
                <Target className="w-5 h-5 text-v3-golddeep" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div className="text-[11.5px] font-semibold text-v3-golddeep">
                  {c.falta.empieza}
                </div>
                <div className="text-[17px] font-bold tracking-[-.02em] mt-1 leading-[1.3] text-v3-ink">
                  {c.falta.accion1}
                </div>
                <div className="text-[12.5px] text-v3-muted mt-1">{c.falta.area1}</div>
              </div>
            </div>

            {[
              { n: 2, accion: c.falta.accion2, area: c.falta.area2 },
              { n: 3, accion: c.falta.accion3, area: c.falta.area3 },
            ].map((x) => (
              <div
                key={x.n}
                className="bg-white rounded-2xl px-4.5 py-4 flex items-center gap-3.5 v3-shadow"
                style={{ padding: '15px 18px' }}
              >
                <span className="w-6 h-6 rounded-full bg-v3-sunken text-v3-muted text-xs font-bold flex items-center justify-center shrink-0">
                  {x.n}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-semibold tracking-[-.01em] leading-[1.35] text-v3-ink">
                    {x.accion}
                  </span>
                  <span className="block text-xs text-v3-faint mt-0.5">{x.area}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {auditorActivo ? (
        <div className="bg-v3-goldsoft rounded-[20px] px-6 py-7 md:px-7 md:py-[30px] mt-6 mx-auto max-w-[613px]">
          <div className="flex items-center gap-6 flex-wrap">
            <div
              aria-hidden="true"
              className="w-[104px] h-[104px] rounded-full flex items-center justify-center shrink-0"
              style={{ background: 'conic-gradient(#feae2c 0 72%, #ececea 72% 100%)' }}
            >
              <div className="w-20 h-20 rounded-full bg-white flex flex-col items-center justify-center">
                <span className="text-[30px] font-bold tracking-[-.03em] leading-none text-[#d4d4d4]">
                  ??
                </span>
                <span className="text-[10px] text-v3-faint mt-0.5">/ 100</span>
              </div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[20px] md:text-[22px] font-bold tracking-[-.02em] leading-[1.2] text-v3-ink">
                {c.auditor.titulo}
              </div>
              <div className="text-[15px] leading-[1.55] text-v3-body mt-2">{c.auditor.cuerpo}</div>
            </div>
          </div>

          {/* Sin su cabecera: el titular y el microcopy los pone la caja del
              diseño, que están arriba y abajo de esto. Y sin su `max-w-3xl mx-auto
              px-6`, que dentro de una caja de 613 px con su propio padding
              metería un margen de más. */}
          <div className="mt-5">
            <AuditorSeo conCabecera={false} className="" />
          </div>

          <div className="text-[12.5px] text-v3-muted mt-2.5">{c.auditor.microcopy}</div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * ¿Está encendido el auditor público?
 *
 * `null` mientras no se sabe, y se trata como «no»: más vale que la caja
 * aparezca un instante tarde a que aparezca y desaparezca. Ante un error de red
 * tampoco se pinta, que es el mismo criterio que sigue `AuditorSeo` — para algo
 * que cuesta dinero, la dirección segura es callarse.
 */
function useAuditorActivo() {
  const [activo, setActivo] = useState(null);

  useEffect(() => {
    let vivo = true;
    // `getAuditStatus` devuelve un BOOLEANO, no el `{enabled}` del endpoint: ya
    // desenvuelve la respuesta y traga los errores por su cuenta. Leerlo como
    // objeto (`r?.enabled`) da `undefined` sobre `true`, y la caja no se monta
    // nunca ni con la función encendida — que es justo lo que pasó.
    getAuditStatus()
      .then((encendido) => {
        if (vivo) setActivo(encendido === true);
      })
      .catch(() => {
        if (vivo) setActivo(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  return activo === true;
}
